/**
 * Hvězdičkové hodnocení.
 *
 * Dvě podoby: `readonly` jen zobrazuje průměr (i s půlhvězdou), interaktivní
 * nechá zákazníka klepnout na počet hvězd. Půlhvězda se kreslí ořezem přes
 * `clip-path`, ne druhým obrázkem – jedna značka drží ostrá v každé velikosti.
 *
 * Barva a počet nikdy nestojí samy: u čísla je vždy popisek pro odečítač
 * („4,3 z 5"), takže hodnocení dává smysl i bez rozlišování odstínů.
 */

export function Stars({
  value,
  count,
  size = 'sm',
}: {
  value: number;
  count?: number;
  size?: 'sm' | 'lg';
}) {
  // Bez jediné recenze se hvězdy nekreslí – pět prázdných by vypadalo jako
  // hodnocení „nula z pěti", což není totéž co „zatím nehodnoceno".
  if (count === 0) {
    return <span className="stars-empty muted small">Zatím bez hodnocení</span>;
  }

  const rounded = Math.round(value * 2) / 2;

  return (
    <span className={`stars stars-${size}`}>
      <span className="stars-track" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((index) => {
          const fill = Math.max(0, Math.min(1, rounded - index));
          return (
            <span key={index} className="star">
              <span className="star-bg">★</span>
              <span className="star-fill" style={{ width: `${fill * 100}%` }}>
                ★
              </span>
            </span>
          );
        })}
      </span>
      <span className="stars-value">
        {value.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}
        {count !== undefined && <span className="muted"> ({count})</span>}
      </span>
      <span className="visually-hidden">
        {value.toFixed(1)} z 5{count !== undefined ? `, ${count} hodnocení` : ''}
      </span>
    </span>
  );
}

/**
 * Volič hvězd pro formulář recenze.
 *
 * Je to sada rádiových přepínačů, ne klikací <span>y: funguje z klávesnice
 * a odečítač ho ohlásí jako „vyber hodnocení". Vizuální hvězdy jsou jen
 * náhrada za puntíky.
 */
export function StarPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (rating: number) => void;
}) {
  return (
    <span className="star-picker" role="radiogroup" aria-label="Hodnocení hvězdami">
      {[1, 2, 3, 4, 5].map((star) => (
        <label key={star} className={star <= value ? 'star-pick is-on' : 'star-pick'}>
          <input
            type="radio"
            name="rating"
            value={star}
            checked={value === star}
            onChange={() => onChange(star)}
          />
          <span aria-hidden="true">★</span>
          <span className="visually-hidden">{star} z 5</span>
        </label>
      ))}
    </span>
  );
}
